import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import Button from "../../components/Button";
import Colors from "../../styles/Colors";
import { useAppDispatch } from "../../hooks";
import { getFactoryPositions } from "../../redux/factoryAction";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight, faGear } from "@fortawesome/free-solid-svg-icons";
import SimulatorModal from "./Modals/SimulatorModal";
import LabelWithHint from "../../components/LabelWithHint";
import { H3, H4 } from "../../styles/Fonts";
import InputRange from "react-input-range";
import { getTruncNum } from "../../utils/common";
import { PairProps } from "../../utils/constant/pairs";
import { useWeb3React } from "@web3-react/core";
import useLendingPoolState from "../../hooks/useLendingPoolState";
import { StateProps } from "../../hooks/useUniswapPoolState";
import { Leverage05, Leverage10, Leverage15, Leverage20 } from "./LeverageItem";
import { openFactoryPosition } from "../../utils/api/factoryV3Api";
import useIsUnderCorrectNetwork from "../../hooks/useIsUnderCorrectNetwork";
import InputWithFloatingLabel from "../../components/InputWithFloatingLabel";
import SetSlippage from "./Modals/SetSlippage";
import { approveFactoryToUseBalanceVault } from "../../utils/api/balanceVaultApi";

const leverages = [0.5, 1, 1.5, 2];
const BACK_TEST_PERIOD = 3;
const DEFAULT_SLIPPAGE = 0.5;

const SettingColumn: React.FC<{
  pair: PairProps;
  updateAccountBalance: () => void;
  uniPoolState: StateProps;
  uniPoolApr: number;
  isApprovedFactory: boolean;
  updateIsApprovedState: () => void;
}> = (props) => {
  const {
    pair,
    updateAccountBalance,
    uniPoolState,
    uniPoolApr,
    isApprovedFactory,
    updateIsApprovedState,
  } = props;
  const {
    token: currency,
    wantToken,
    borrowToken,
    factoryAddress,
    lendingPoolAddress,
    deployedChainId,
  } = pair;
  const { tokenSymbol, tokenAddress, tokenDecimal } = wantToken;
  const { tokenSymbol: borrowTokenSymbol, tokenDecimal: bTokenDecimal } =
    borrowToken;

  const { account, chainId, library } = useWeb3React();

  const { borrowRate, updateLendingPoolState } = useLendingPoolState(
    lendingPoolAddress,
    deployedChainId
  );
  const isUnderCorrectNetwork = useIsUnderCorrectNetwork();

  // Open Position Parameters
  const [range, setRange] = useState<number>(15);
  const [stopLossLowerPrice, setStopLossLowerPrice] = useState<number>(0);
  const [stopLossUpperPrice, setStopLossUpperPrice] = useState<number>(0);
  const [leverage, setLeverage] = useState<number>(1);
  const [investAmount, setInvestAmount] = useState<number>(0);
  const [slippage, setSlippage] = useState<number>(DEFAULT_SLIPPAGE);

  // Modals
  const [showSimulator, setShowSimulator] = useState<boolean>(false);
  const [slippageIsShow, setSlippageIsShow] = useState<boolean>(false);

  const [estimatedFeeApy, setEstimatedFeeApy] = useState<number>(0);
  const [isOpening, setIsOpening] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);

  const rangeIsValid = range < 100 && range > 0;
  const stopLossIsValid =
    stopLossUpperPrice > uniPoolState?.token0Price &&
    stopLossLowerPrice > 0 &&
    stopLossLowerPrice < uniPoolState?.token0Price;

  const formRef = useRef<HTMLFormElement>(null);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (range) {
      const _estimatedFeeApy = (50 / range) * uniPoolApr;
      setEstimatedFeeApy(_estimatedFeeApy);
    }
  }, [range, uniPoolApr]);

  const initSetting = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setRange(15);
    setLeverage(1);
    setInvestAmount(0);
    setStopLossLowerPrice(0);
    setStopLossUpperPrice(0);
    setSlippage(DEFAULT_SLIPPAGE);
  };

  const positionParamsIsValid = useMemo(
    () =>
      !!(
        investAmount &&
        stopLossIsValid &&
        leverage &&
        rangeIsValid &&
        slippage
      ),
    [investAmount, stopLossIsValid, leverage, rangeIsValid, slippage]
  );

  const handleOpenPosition = async () => {
    if (
      account &&
      factoryAddress &&
      positionParamsIsValid &&
      chainId &&
      library
    ) {
      setIsOpening(true);

      await openFactoryPosition(
        account,
        factoryAddress,
        investAmount,
        tokenAddress,
        tokenSymbol,
        tokenDecimal,
        stopLossUpperPrice,
        stopLossLowerPrice,
        leverage,
        range / 100,
        chainId,
        bTokenDecimal,
        uniPoolState.tickSpacing,
        uniPoolState.tick, // spotPriceTick,
        slippage / 100, //slippage,
        library
      );

      setIsOpening(false);

      initSetting();
      dispatch(
        getFactoryPositions(
          account,
          pair.factoryAddress,
          wantToken.tokenAddress,
          wantToken.tokenDecimal,
          borrowToken.tokenAddress,
          borrowToken.tokenDecimal,
          pair.uniPoolAddress,
          pair.lendingPoolAddress,
          chainId,
          library
        )
      );
      updateLendingPoolState();
      updateAccountBalance();
    }
  };

  const handleApproveFactory = async () => {
    if (account && factoryAddress && chainId && library) {
      setIsApproving(true);

      await approveFactoryToUseBalanceVault(
        account,
        factoryAddress,
        tokenAddress,
        chainId,
        library
      );

      setIsApproving(false);
      updateIsApprovedState();
    }
  };

  const openBtnIsDisabled =
    isOpening || !(account && positionParamsIsValid) || !isUnderCorrectNetwork;

  const approveBtnIsDisabled =
    isApproving || !account || !isUnderCorrectNetwork;

  const getLowerPrice = (_range: number) => {
    const currentTick = uniPoolState?.tick;
    return _range && currentTick
      ? 1.0001 ** (currentTick - _range * 100) *
          10 ** (bTokenDecimal - tokenDecimal)
      : "-";
  };

  const getUpperPrice = (_range: number) => {
    const currentTick = uniPoolState?.tick;
    return _range && currentTick
      ? 1.0001 ** (currentTick + _range * 100) *
          10 ** (bTokenDecimal - tokenDecimal)
      : "-";
  };

  return (
    <OuterContainer>
      <Title>Create UniBot</Title>
      <IntroText>
        {`Unibot is an Uniswap V3 Automated Leveraged Liquidity Provision
        Platform. You can open your automated V3 liquidity provision with ${wantToken.tokenSymbol}
        and borrow ${borrowToken.tokenSymbol} to hedge and increase your transaction fee return.`}
      </IntroText>
      <Container className="fac-s-2">
        <Form ref={formRef}>
          <SectionContainer>
            <LabelWithHint
              labelComponent={<Label>Range</Label>}
              hintText="How wide the provided liquidity will be distributed with the liquidity centers at the entry price"
            />
            <InputRangeContainer>
              <InputRange
                minValue={uniPoolState?.tickSpacing / 100 || 0}
                maxValue={100}
                value={range}
                step={uniPoolState?.tickSpacing / 100}
                onChange={(value) => setRange(+getTruncNum(+value, 3))}
                formatLabel={(value) => `${value}%`}
                disabled={!uniPoolState?.tickSpacing}
              />
            </InputRangeContainer>
            <ClosePriceRow>
              <ItalicText>{`ranges from 1 ${borrowTokenSymbol} = `}</ItalicText>
              <PaddingText>
                {rangeIsValid ? `${getLowerPrice(range)}` : "-"}
              </PaddingText>
              <MiddleText>{`${tokenSymbol} to`}</MiddleText>
              <PaddingText>
                {rangeIsValid ? `${getUpperPrice(range)}` : "-"}
              </PaddingText>
              <MiddleText>{`${tokenSymbol}`}</MiddleText>
            </ClosePriceRow>
          </SectionContainer>
          <div>
            <LabelWithHint
              labelComponent={<Label>Leverage</Label>}
              hintText={`The ratio of the total position value divided by your initial investment in ${wantToken.tokenSymbol}. With leverage > 2x, users get to create bearish strategies with max profit occurs when price drops, while with leverage <2x, the strategies become are relatively bullish with max profit occurs when price rises.`}
            />
            <LeverageButtonsContainer>
              {leverages.map((ratio) => (
                <LeverageBtn
                  key={ratio}
                  isSelect={leverage === ratio}
                  onClick={() => setLeverage(ratio)}
                >
                  {ratio + 1 + "x"}
                </LeverageBtn>
              ))}
            </LeverageButtonsContainer>
            <Output>
              {leverage === 2 && <Leverage20 />}
              {leverage === 1.5 && <Leverage15 />}
              {leverage === 1 && <Leverage10 />}
              {leverage === 0.5 && <Leverage05 />}
            </Output>
          </div>
          <div>
            <BalanceRow>
              <LabelWithHint
                labelComponent={<BLabel>Est. Fee APR</BLabel>}
                hintText="The estimated annualized return from fee income based on the liquidity distribution and fee income in the last 3 days"
              />
              <HighlightValue>{`${
                rangeIsValid && estimatedFeeApy
                  ? (estimatedFeeApy * (1 + leverage)).toFixed(2)
                  : "-"
              } %`}</HighlightValue>
            </BalanceRow>
          </div>
          <div>
            <BalanceRow>
              <LabelWithHint
                labelComponent={<BLabel>Borrowing Rate</BLabel>}
                hintText={`The expected cost of borrowing ${borrowToken.tokenSymbol}`}
              />
              <BValue>{`${(borrowRate * 100).toFixed(2)} %`}</BValue>
            </BalanceRow>
          </div>
          <div>
            <LabelWithHint
              labelComponent={<Label>Stop Loss</Label>}
              hintText="When the oracle price reaches the stop loss prices, our keeper will close your position"
            />
            <PriceRow>
              <InputWithFloatingLabel
                labelText={"Lower Price"}
                value={stopLossLowerPrice}
                setValue={setStopLossLowerPrice}
                prefix=""
                suffix={wantToken.tokenSymbol}
              />
              <InputWithFloatingLabel
                labelText={"Upper Price"}
                value={stopLossUpperPrice}
                setValue={setStopLossUpperPrice}
                prefix=""
                suffix={wantToken.tokenSymbol}
              />
            </PriceRow>
          </div>
          <SectionContainer>
            <InputContainer>
              <LabelContainer>
                <LabelWithHint
                  labelComponent={<Label>Amount</Label>}
                  hintText={`The investment in ${wantToken.tokenSymbol}`}
                />
              </LabelContainer>
              <Input
                type="number"
                placeholder="100"
                onChange={(e) => {
                  setInvestAmount(+e.target.value);
                }}
              />
              <Unit>{currency}</Unit>
            </InputContainer>
          </SectionContainer>
        </Form>
        <BtnContainer>
          {isApprovedFactory ? (
            <ButtonRow>
              <Button
                btnText={"Create"}
                btnType="primary"
                onClick={handleOpenPosition}
                disabled={openBtnIsDisabled}
                isLoading={isOpening}
              />
              <SettingBtn onClick={() => setSlippageIsShow(true)}>
                <SettingIcon icon={faGear} />
              </SettingBtn>
            </ButtonRow>
          ) : (
            <Button
              btnText={"Approve Factory"}
              btnType="primary"
              onClick={handleApproveFactory}
              disabled={approveBtnIsDisabled}
              isLoading={isApproving}
            />
          )}
          <SimulateBtn
            onClick={() =>
              !!positionParamsIsValid &&
              !!uniPoolState?.token0Price &&
              setShowSimulator(true)
            }
            disabled={!(positionParamsIsValid && uniPoolState?.token0Price)}
          >
            <Span>Simulate Your Position</Span>
            <Icon icon={faArrowRight} />
          </SimulateBtn>
        </BtnContainer>
        <SimulatorModal
          simulateData={{
            usdcAmount: investAmount,
            borrowingRatio: leverage,
            duration: BACK_TEST_PERIOD,
            range: range,
            borrowRate: borrowRate,
            estimatedFeeApy: estimatedFeeApy,
            stopLossLowerPrice: stopLossLowerPrice,
            stopLossUpperPrice: stopLossUpperPrice,
          }}
          marketPrice={uniPoolState?.token0Price}
          setShowModal={setShowSimulator}
          isShowModal={showSimulator}
        />
        <SetSlippage
          isShowModal={slippageIsShow}
          setShowModal={setSlippageIsShow}
          slippage={slippage}
          setSlippage={setSlippage}
          defaultSlippage={DEFAULT_SLIPPAGE}
        />
      </Container>
    </OuterContainer>
  );
};

export default SettingColumn;

const OuterContainer = styled.div`
  padding: 24px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const SectionContainer = styled.div`
  margin-top: 12px;
`;

const LabelContainer = styled.div`
  position: absolute;
  padding: 0 4px;
  top: -14px;
  left: 8px;
  background-color: ${Colors.backgroundGray1};
`;

const Label = styled(H4)`
  color: ${Colors.gray3};
  font-size: 13px;
  line-height: 22px;
  @media (max-width: 768px) {
  }
`;

const InputRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const InputContainer = styled.div`
  flex: 1 1 60px;
  padding: 6px 12px 6px 12px;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  border: 1px solid ${Colors.gray4};
`;

const Input = styled.input`
  width: 100%;
  border: none;
  background-color: transparent;
  font-size: 14px;
  line-height: 14px;
  font-weight: bold;
  color: ${Colors.ivory};
  :focus {
    border: none;
    outline: none;
  }
`;

const Unit = styled.span`
  font-size: 13px;
  line-height: 22px;
  color: ${Colors.ivory};
  margin: 0 8px;
`;

const Output = styled.output`
  height: 28px;
  margin-top: 12px;
  font-size: 16px;
  line-height: 28px;
  color: ${Colors.ivory};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  background-color: ${Colors.backgroundGray4};
`;

const ClosePriceRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 8px;
`;

const PaddingText = styled(H4)`
  min-width: 30px;
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: center;
  background-color: ${Colors.backgroundGray4};
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 13px;
  line-height: 22px;
  font-weight: bold;
  color: ${Colors.gray4};
  margin: 0 6px;
`;

const BtnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  button {
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 16px;
    line-height: 24px;
    @media (max-width: 768px) {
      font-size: 14px;
      line-height: 20px;
    }
  }
`;

const BalanceRow = styled(InputRow)`
  justify-content: space-between;
`;

const BLabel = styled(Label)`
  margin-bottom: 0px;
`;

const BValue = styled(Unit)`
  margin: 0;
  font-size: 14px;
  font-weight: bold;
`;

const LeverageButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 8px;
`;

const LeverageBtn = styled.div<{ isSelect: boolean }>`
  flex: 1;
  background-color: rgba(100, 100, 100, 0.4);
  padding: 2px;
  font-size: 12px;
  line-height: 22px;
  font-weight: ${(props) => (props.isSelect ? "bold" : "normal")};
  border-radius: 4px;
  color: ${(props) => (props.isSelect ? Colors.lightKhaki : Colors.ivory)};
  border: 1px solid
    ${(props) => (props.isSelect ? Colors.lightKhaki : "transparent")};
  cursor: pointer;
  text-align: center;
`;

const ItalicText = styled.span`
  display: block;
  font-size: 13px;
  line-height: 22px;
  color: ${Colors.gray3};
  font-style: italic;
  margin: 4px 0 4px 0;
`;

const MiddleText = styled.span`
  font-size: 13px;
  line-height: 22px;
  color: ${Colors.gray3};
`;

const SimulateBtn = styled.div<{ disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 4px;
  padding: 4px 12px;
  color: ${Colors.ivory};
  border-radius: 4px;
  border: 1px solid ${Colors.ivory};
  cursor: ${(props) => (props.disabled ? "inherit" : "pointer")};
  opacity: ${(props) => (props.disabled ? "0.6" : "1")};
  :hover {
    color: ${(props) => (props.disabled ? Colors.ivory : Colors.lightKhaki)};
    border: 1px solid
      ${(props) => (props.disabled ? Colors.ivory : Colors.lightKhaki)};
  }
`;

const Icon = styled(FontAwesomeIcon)`
  margin-left: 6px;
  font-size: 14px;
  font-weight: bold;
`;

const Span = styled.span`
  font-size: 14px;
  line-height: 24px;
`;

const Title = styled(H3)`
  color: ${Colors.ivory};
  font-weight: bold;
  font-size: 15px;
  line-height: 24px;
  margin-bottom: 8px;
`;

const IntroText = styled(H4)`
  color: ${Colors.gray3};
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 16px;
`;

const HighlightValue = styled(BValue)`
  color: ${Colors.green};
  font-weight: bold;
`;

const InputRangeContainer = styled.div`
  padding: 24px 0 20px 0px;

  .input-range__label--max {
    right: 20px;
  }
  .input-range__label--min {
    left: 12px;
  }
`;

const PriceRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const SettingBtn = styled.button`
  width: 40px;
  border-radius: 8px;
  border: 1px solid ${Colors.ivory};
  :active {
    transform: scale(0.95);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
`;

const SettingIcon = styled(FontAwesomeIcon)`
  font-size: 16px;
  color: ${Colors.gray3};
  transform: rotate(-30deg);
`;
